import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AlertInterval,
    DeviceCare,
    DeviceCareInstallation,
    FeatureState,
    Metric,
    MetricsStatus,
    Report,
    SiteFeature,
    SmtpStatus,
} from '@myrtls/api-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    first,
    map,
    mapTo,
    mergeMap,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { InfluxdbService } from '../../core/services/influxdb.service';
import { MetricDataFetchType } from '../../shared/models/metric-data-fetch-type.enum';
import { MetricWithData } from '../../shared/models/metric-with-data.model';
import {
    ACTIVATE_ENDPOINT,
    ALERTS_ENDPOINT,
    CLEAR_ENDPOINT,
    CURRENT_ALERTS_ENDPOINT,
    DEVICE_CARE_ENDPOINT,
    INSTALLATION_ENDPOINT,
    METRIC_OVERRIDES_ENDPOINT,
    METRICS_ENDPOINT,
    REPORTS_ENDPOINT,
    RESET_ENDPOINT,
    SITES_API_URL,
    SMTP_ENDPOINT,
    SNACK_BAR_DURATION,
    TEST_ENDPOINT,
} from '../../shared/utils/constants';
import { AppState } from '../index';
import { showSnackBarAction } from '../snack-bar/snack-bar.actions';
import { $selectedSiteId } from '../sites/sites.selectors';
import {
    $deviceCareConfig,
    $metricsWithData,
    $selectedPeriod,
} from './monitoring.selectors';
import { SnackBarType } from '../../shared/models/snack-bar-config.model';
import { combineLatest, EMPTY, from as fromPromise, interval, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
    activateDeviceCareFailureAction,
    activateDeviceCareRequestAction,
    activateDeviceCareSuccessAction,
    clearDeviceCareFailureAction,
    clearDeviceCareRequestAction,
    clearDeviceCareSuccessAction,
    getCurrentSiteAlertsFailureAction,
    getCurrentSiteAlertsRequestAction,
    getCurrentSiteAlertsSuccessAction,
    getSiteAlertsFailureAction,
    getSiteAlertsRequestAction,
    getSiteAlertsSuccessAction,
    getSiteDeviceCareConfigFailureAction,
    getSiteDeviceCareConfigRequestAction,
    getSiteDeviceCareConfigSuccessAction,
    getSiteDeviceCareInstallationFailureAction,
    getSiteDeviceCareInstallationRequestAction,
    getSiteDeviceCareInstallationSuccessAction,
    getSiteMetricDashboardDataFailureAction,
    getSiteMetricDashboardDataRequestAction,
    getSiteMetricDashboardDataSuccessAction,
    getSiteMetricDataFailureAction,
    getSiteMetricDataRequestAction,
    getSiteMetricDataSuccessAction,
    getSiteMetricFilteringDataFailureAction,
    getSiteMetricFilteringDataRequestAction,
    getSiteMetricFilteringDataSuccessAction,
    getSiteMetricsConfigFailureAction,
    getSiteMetricsConfigRequestAction,
    getSiteMetricsConfigSuccessAction,
    getSiteMetricsDataRequestAction,
    getSiteMetricsStatusFailureAction,
    getSiteMetricsStatusSuccessAction,
    getSiteReportRequestAction,
    getSiteReportsFailureAction,
    getSiteReportsRequestAction,
    getSiteReportsSuccessAction,
    getSiteSmtpStatusFailureAction,
    getSiteSmtpStatusRequestAction,
    getSiteSmtpStatusSuccessAction,
    resetDeviceCareSettingsFailureAction,
    resetDeviceCareSettingsRequestAction,
    resetDeviceCareSettingsSuccessAction,
    selectPeriodDateRangeAction,
    startPoolingSiteMetricsStatusAction,
    stopPoolingSiteMetricsStatusAction,
    updateSiteMetricOverridesFailureAction,
    updateSiteMetricOverridesRequestAction,
    updateSiteMetricOverridesSuccessAction,
    updateSiteMetricSettingsFailureAction,
    updateSiteMetricSettingsRequestAction,
    updateSiteMetricSettingsSuccessAction,
    updateSiteReportSettingsFailureAction,
    updateSiteReportSettingsRequestAction,
    updateSiteReportSettingsSuccessAction,
} from './monitoring.actions';
import {
    getSiteFeaturesFailureAction,
    getSiteFeaturesRequestAction,
    getSiteFeaturesSuccessAction,
} from '../sites/sites.actions';
import { DeviceCareInfo } from './monitoring.reducer';
import moment from 'moment';
import { Location } from '@angular/common';
import { RoutePath } from '../../shared/models/route-path.enum';
import { Router } from '@angular/router';

@Injectable()
export class MonitoringEffects {
    private httpWithoutAuth: HttpClient;

    getSiteMetricsConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteMetricsConfigRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.route.queryParams,
            ),
            switchMap(([{ dataFetch }, siteId, params]) => {
                if (params.serverToken) {
                    return this.httpWithoutAuth
                        .get<Metric[]>(
                            `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${METRICS_ENDPOINT}`,
                            {
                                headers: {
                                    Authorization:
                                        'Bearer ' + params.serverToken,
                                },
                            },
                        )
                        .pipe(
                            map(metrics =>
                                getSiteMetricsConfigSuccessAction({
                                    siteId,
                                    metrics,
                                    dataFetch,
                                }),
                            ),
                            catchError(error =>
                                of(
                                    getSiteMetricsConfigFailureAction({
                                        error,
                                    }),
                                ),
                            ),
                        );
                } else {
                    return this.http
                        .get<
                            Metric[]
                        >(`${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${METRICS_ENDPOINT}`)
                        .pipe(
                            map(metrics =>
                                getSiteMetricsConfigSuccessAction({
                                    siteId,
                                    metrics,
                                    dataFetch,
                                }),
                            ),
                            catchError(error =>
                                of(
                                    getSiteMetricsConfigFailureAction({
                                        error,
                                    }),
                                ),
                            ),
                        );
                }
            }),
        ),
    );

    getSiteMetricsDataFromPeriod$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectPeriodDateRangeAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((siteId): siteId is string => siteId != null),
                ),
                this.store.pipe(select($metricsWithData)),
            ),
            tap(([period]) => {
                const queryParams = new URLSearchParams(window.location.search);
                queryParams.set(
                    'from',
                    moment(period.from).format('YYYY-MM-DD'),
                );
                queryParams.set('to', moment(period.to).format('YYYY-MM-DD'));
                this.location.replaceState(
                    window.location.pathname,
                    queryParams.toString(),
                );
            }),
            map(([, siteId, metrics]) => {
                return getSiteMetricsDataRequestAction({
                    siteId,
                    metrics,
                });
            }),
        ),
    );

    getSiteMetricsDashboardData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteMetricsConfigSuccessAction),
            filter(
                ({ dataFetch }) =>
                    dataFetch === MetricDataFetchType.DASHBOARD ||
                    dataFetch === MetricDataFetchType.ALL,
            ),
            switchMap(({ siteId, metrics }) =>
                this.store.pipe(
                    select($deviceCareConfig),
                    filter(
                        (config): config is DeviceCareInfo => config != null,
                    ),
                    take(1),
                    map(deviceCare => ({
                        deviceCare,
                        metrics,
                        siteId,
                    })),
                ),
            ),
            mergeMap(({ siteId, metrics, deviceCare }) =>
                metrics
                    .filter(metric => metric.dashboardTemplate)
                    .filter(
                        metric =>
                            metric.dashboardTemplate?.query != null &&
                            metric.dashboard,
                    )
                    .map(
                        ({
                            id,
                            dashboardTemplate,
                            overrides,
                            thresholdValue,
                        }) =>
                            getSiteMetricDashboardDataRequestAction({
                                siteId,
                                metricId: id,
                                threshold: thresholdValue,
                                query: dashboardTemplate?.query || '',
                                deviceCare,
                                overrides:
                                    overrides?.map(
                                        override => override.value,
                                    ) || [],
                            }),
                    ),
            ),
        ),
    );

    getSiteMetricDashboardData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteMetricDashboardDataRequestAction),
            mergeMap(
                ({
                    metricId,
                    query,
                    siteId,
                    deviceCare: {
                        influx: { host, organization, readToken, bucket },
                        selectedPeriod,
                    },
                    overrides,
                    threshold,
                }) => {
                    return fromPromise(
                        this.influxDbService.getDashboardChartData({
                            apiUrl: host,
                            org: organization,
                            token: readToken,
                            query,
                            bucket,
                            overrides,
                            to: selectedPeriod?.to,
                            threshold,
                        }),
                    ).pipe(
                        map(data => {
                            return getSiteMetricDashboardDataSuccessAction({
                                metricId,
                                siteId,
                                data,
                            });
                        }),
                        catchError(error =>
                            of(
                                getSiteMetricDashboardDataFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
                },
            ),
        ),
    );

    getSiteMetricsHistoryData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                getSiteMetricsDataRequestAction,
                getSiteMetricsConfigSuccessAction,
            ),
            filter(action => {
                return (
                    action.type !== getSiteMetricsConfigSuccessAction.type ||
                    action.dataFetch === MetricDataFetchType.HISTORY ||
                    action.dataFetch === MetricDataFetchType.ALL
                );
            }),
            switchMap(action => {
                return combineLatest([
                    this.store.pipe(select($deviceCareConfig)),
                ]).pipe(
                    filter(
                        (config): config is [DeviceCareInfo] =>
                            config[0] !== null && config[0] !== undefined,
                    ),
                    take(1),
                    map(([deviceCare]) => ({
                        deviceCare,
                        metrics: action.metrics as MetricWithData[],
                        siteId: action.siteId,
                    })),
                );
            }),
            mergeMap(({ siteId, metrics, deviceCare }) =>
                metrics
                    .filter(
                        ({ historyTemplate }) => historyTemplate !== undefined,
                    )
                    .map(({ id, historyTemplate, overrides }) => {
                        return getSiteMetricDataRequestAction({
                            siteId,
                            metricId: id,
                            query: historyTemplate?.query || '',
                            deviceCare,
                            overrides:
                                overrides?.map(override => override.value) ||
                                [],
                        });
                    }),
            ),
        ),
    );

    getSiteMetricData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteMetricDataRequestAction),
            withLatestFrom(this.store.select($selectedPeriod)),
            mergeMap(
                ([
                    {
                        metricId,
                        query,
                        siteId,
                        deviceCare: {
                            influx: { host, organization, readToken, bucket },
                        },
                        overrides,
                    },
                    selectedPeriod,
                ]) => {
                    if (!selectedPeriod) {
                        return EMPTY;
                    }

                    return fromPromise(
                        this.influxDbService.getChartData({
                            apiUrl: host,
                            org: organization,
                            token: readToken,
                            query,
                            bucket,
                            from: selectedPeriod.from,
                            to: selectedPeriod.to,
                            dateRange: selectedPeriod.dateRange,
                            overrides,
                        }),
                    ).pipe(
                        map(data =>
                            getSiteMetricDataSuccessAction({
                                metricId,
                                siteId,
                                data,
                                range: selectedPeriod.dateRange,
                                from: selectedPeriod.from,
                                to: selectedPeriod.to,
                            }),
                        ),
                        catchError(error =>
                            of(getSiteMetricDataFailureAction({ error })),
                        ),
                    );
                },
            ),
        ),
    );

    getSiteMetricFilteringData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteMetricFilteringDataRequestAction),
            withLatestFrom(
                this.store
                    .select($selectedSiteId)
                    .pipe(filter((x): x is string => !!x)),
                this.store
                    .select($deviceCareConfig)
                    .pipe(filter((x): x is DeviceCareInfo => !!x)),
                this.store.select($metricsWithData),
            ),
            mergeMap(([{ metricId }, siteId, deviceCare, metrics]) => {
                return fromPromise(
                    this.influxDbService.getDashboardChartData({
                        apiUrl: deviceCare.influx.host,
                        org: deviceCare.influx.organization,
                        token: deviceCare.influx.readToken,
                        bucket: deviceCare.influx.bucket,
                        query:
                            metrics.find(metric => metric.id === metricId)
                                ?.dashboardTemplate?.query || '',
                        overrides: [],
                    }),
                ).pipe(
                    map(data =>
                        getSiteMetricFilteringDataSuccessAction({
                            metricId,
                            siteId,
                            data: InfluxdbService.mapRowsToFilteringData(
                                data,
                                metrics.find(metric => metric.id === metricId)
                                    ?.labelGroups || [],
                            ),
                        }),
                    ),
                    catchError(error =>
                        of(
                            getSiteMetricFilteringDataFailureAction({
                                error,
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    updateSiteMetricSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSiteMetricSettingsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ metricPutRequest }, siteId]) =>
                this.http
                    .put<
                        Metric[]
                    >(`${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${METRICS_ENDPOINT}`, metricPutRequest)
                    .pipe(
                        mergeMap(metrics => {
                            return [
                                showSnackBarAction({
                                    config: {
                                        type: SnackBarType.SUCCESSFUL,
                                        duration: SNACK_BAR_DURATION,
                                        description:
                                            'Metric settings have been updated successfully!',
                                    },
                                }),
                                updateSiteMetricSettingsSuccessAction({
                                    metrics,
                                    siteId,
                                }),
                            ];
                        }),
                        catchError(error =>
                            of(
                                updateSiteMetricSettingsFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    updateSiteMetricOverrides$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSiteMetricOverridesRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ metricId, metricOverridesPutRequest }, siteId]) =>
                this.http
                    .put(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${METRICS_ENDPOINT}/${metricId}/${METRIC_OVERRIDES_ENDPOINT}`,
                        metricOverridesPutRequest,
                    )
                    .pipe(
                        mergeMap(() => {
                            return [
                                showSnackBarAction({
                                    config: {
                                        type: SnackBarType.SUCCESSFUL,
                                        duration: SNACK_BAR_DURATION,
                                        description:
                                            'Metric has been updated successfully!',
                                    },
                                }),
                                updateSiteMetricOverridesSuccessAction(),
                                getSiteMetricsConfigRequestAction({
                                    dataFetch: MetricDataFetchType.NONE,
                                }),
                            ];
                        }),
                        catchError(error =>
                            of(
                                updateSiteMetricOverridesFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    getSiteDeviceCareConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteDeviceCareConfigRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.route.queryParams,
            ),
            switchMap(([{ period }, siteId, params]) => {
                if (params.serverToken) {
                    return this.httpWithoutAuth
                        .get<DeviceCare>(
                            `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}`,
                            {
                                headers: {
                                    Authorization:
                                        'Bearer ' + params.serverToken,
                                },
                            },
                        )
                        .pipe(
                            map(config =>
                                getSiteDeviceCareConfigSuccessAction({
                                    siteId,
                                    config,
                                    period,
                                }),
                            ),
                            catchError(error =>
                                of(
                                    getSiteDeviceCareConfigFailureAction({
                                        error,
                                    }),
                                ),
                            ),
                        );
                } else {
                    return this.http
                        .get<DeviceCare>(
                            `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}`,
                        )
                        .pipe(
                            map(config =>
                                getSiteDeviceCareConfigSuccessAction({
                                    siteId,
                                    config,
                                    period,
                                }),
                            ),
                            catchError(error =>
                                of(
                                    getSiteDeviceCareConfigFailureAction({
                                        error,
                                    }),
                                ),
                            ),
                        );
                }
            }),
        ),
    );

    getDeviceCareInstallation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteDeviceCareInstallationRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<DeviceCareInstallation>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${INSTALLATION_ENDPOINT}`,
                    )
                    .pipe(
                        map(installation =>
                            getSiteDeviceCareInstallationSuccessAction({
                                installation,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getSiteDeviceCareInstallationFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    activateDeviceCare$ = createEffect(() =>
        this.actions$.pipe(
            ofType(activateDeviceCareRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .put<number>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${ACTIVATE_ENDPOINT}`,
                        {},
                    )
                    .pipe(
                        mergeMap(date => [
                            getSiteFeaturesRequestAction(),
                            activateDeviceCareSuccessAction({
                                date: date,
                                site: siteId,
                            }),
                        ]),
                        catchError(error =>
                            of(
                                activateDeviceCareFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    onDeviceCareActivated$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(activateDeviceCareSuccessAction),
                withLatestFrom(
                    this.store.pipe(
                        select($selectedSiteId),
                        filter((id): id is string => id != null),
                    ),
                ),
                tap(([, siteId]) => {
                    this.actions$
                        .pipe(
                            ofType(getSiteFeaturesSuccessAction),
                            takeUntil(
                                this.actions$.pipe(
                                    ofType(getSiteFeaturesFailureAction),
                                ),
                            ),
                            first(),
                        )
                        .subscribe(() => {
                            console.log(
                                'Navigating to Device Care Dashboards...',
                            );
                            this.router.navigateByUrl(
                                `/${RoutePath.SITE}/${siteId}/${SiteFeature.DEVICE_CARE_DASHBOARD}`,
                            );
                        });
                }),
            ),
        { dispatch: false },
    );

    selectSiteDeviceCareContext$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(getSiteFeaturesSuccessAction),
                tap(features => {
                    if (
                        features.features.find(
                            feature =>
                                feature.name ===
                                SiteFeature.DEVICE_CARE_GRAFANA,
                        )?.state === FeatureState.ENABLED
                    ) {
                        this.http
                            .get(
                                `${SITES_API_URL}/${features.siteId}/${DEVICE_CARE_ENDPOINT}/context`,
                            )
                            .pipe(first())
                            .subscribe();
                    }
                }),
            ),
        { dispatch: false },
    );

    updateSiteReportSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSiteReportSettingsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ deviceCarePutRequest }, siteId]) =>
                this.http
                    .put<DeviceCare>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}`,
                        deviceCarePutRequest,
                    )
                    .pipe(
                        mergeMap(config => {
                            return [
                                showSnackBarAction({
                                    config: {
                                        type: SnackBarType.SUCCESSFUL,
                                        duration: SNACK_BAR_DURATION,
                                        description:
                                            'Report settings have been updated successfully!',
                                    },
                                }),
                                updateSiteReportSettingsSuccessAction({
                                    config,
                                    siteId,
                                }),
                            ];
                        }),
                        catchError(error =>
                            of(
                                updateSiteReportSettingsFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    clearDeviceCare$ = createEffect(() =>
        this.actions$.pipe(
            ofType(clearDeviceCareRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ clearDeviceCareRequest }, siteId]) =>
                this.http
                    .put(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${CLEAR_ENDPOINT}`,
                        clearDeviceCareRequest,
                    )
                    .pipe(
                        mergeMap(() => [
                            clearDeviceCareSuccessAction(),
                            showSnackBarAction({
                                config: {
                                    description:
                                        'Device Care data deletion has been requested.',
                                    type: SnackBarType.SUCCESSFUL,
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(
                                clearDeviceCareFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    resetDeviceCareSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetDeviceCareSettingsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .put(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${METRICS_ENDPOINT}/${RESET_ENDPOINT}`,
                        {},
                    )
                    .pipe(
                        mergeMap(() => [
                            resetDeviceCareSettingsSuccessAction(),

                            getSiteMetricsConfigRequestAction({
                                dataFetch: MetricDataFetchType.NONE,
                            }),

                            showSnackBarAction({
                                config: {
                                    description:
                                        'Device Care settings have been reset.',
                                    type: SnackBarType.SUCCESSFUL,
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(
                                resetDeviceCareSettingsFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    getSiteAlerts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteAlertsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.route.queryParams,
            ),
            switchMap(([, siteId, params]) => {
                let httpAlerts;

                if (params.serverToken) {
                    httpAlerts = this.httpWithoutAuth.get<AlertInterval[]>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${ALERTS_ENDPOINT}`,
                        {
                            headers: {
                                Authorization: 'Bearer ' + params.serverToken,
                            },
                        },
                    );
                } else {
                    httpAlerts = this.http.get<AlertInterval[]>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${ALERTS_ENDPOINT}`,
                    );
                }

                return httpAlerts.pipe(
                    map(alerts =>
                        getSiteAlertsSuccessAction({
                            alerts,
                        }),
                    ),
                    catchError(error =>
                        of(getSiteAlertsFailureAction({ error })),
                    ),
                );
            }),
        ),
    );

    getCurrentSiteAlerts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCurrentSiteAlertsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<
                        AlertInterval[]
                    >(`${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${ALERTS_ENDPOINT}/${CURRENT_ALERTS_ENDPOINT}`)
                    .pipe(
                        map(alerts =>
                            getCurrentSiteAlertsSuccessAction({ alerts }),
                        ),
                        catchError(error =>
                            of(getCurrentSiteAlertsFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    getSiteReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteReportsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<
                        Report[]
                    >(`${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${REPORTS_ENDPOINT}`)
                    .pipe(
                        map(reports =>
                            getSiteReportsSuccessAction({
                                reports: reports.reverse(),
                            }),
                        ),
                        catchError(error =>
                            of(getSiteReportsFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    downloadReport$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(getSiteReportRequestAction),
                withLatestFrom(
                    this.store.pipe(
                        select($selectedSiteId),
                        filter((id): id is string => id != null),
                    ),
                ),
                switchMap(([{ reportId, reportName }, siteId]) =>
                    this.http
                        .get(
                            `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${REPORTS_ENDPOINT}/${reportId}`,
                            { responseType: 'blob' },
                        )
                        .pipe(
                            tap(response => {
                                const a = document.createElement('a');
                                a.href = URL.createObjectURL(response);
                                a.download = reportName;
                                a.click();
                            }),
                            catchError(error =>
                                of(getSiteReportsFailureAction({ error })),
                            ),
                        ),
                ),
            ),
        { dispatch: false },
    );

    getSiteSmtpStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteSmtpStatusRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.route.queryParams,
            ),
            switchMap(([, siteId, params]) => {
                if (params.serverToken) {
                    return this.httpWithoutAuth
                        .get<SmtpStatus>(
                            `${SITES_API_URL}/${siteId}/${SMTP_ENDPOINT}`,
                            {
                                headers: {
                                    Authorization:
                                        'Bearer ' + params.serverToken,
                                },
                            },
                        )
                        .pipe(
                            map(smtpStatus =>
                                getSiteSmtpStatusSuccessAction({
                                    siteId,
                                    smtpStatus,
                                }),
                            ),
                            catchError(error =>
                                of(getSiteSmtpStatusFailureAction({ error })),
                            ),
                        );
                } else {
                    return this.http
                        .get<SmtpStatus>(
                            `${SITES_API_URL}/${siteId}/${SMTP_ENDPOINT}`,
                        )
                        .pipe(
                            map(smtpStatus =>
                                getSiteSmtpStatusSuccessAction({
                                    siteId,
                                    smtpStatus,
                                }),
                            ),
                            catchError(error =>
                                of(getSiteSmtpStatusFailureAction({ error })),
                            ),
                        );
                }
            }),
        ),
    );

    getSiteMetricsStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPoolingSiteMetricsStatusAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ intervalTime }, siteId]) =>
                interval(intervalTime).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(stopPoolingSiteMetricsStatusAction),
                        ),
                    ),
                    mapTo(
                        this.http
                            .get<MetricsStatus>(
                                `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${TEST_ENDPOINT}`,
                            )
                            .pipe(
                                mergeMap(data => [
                                    getSiteMetricsStatusSuccessAction({
                                        status: data,
                                    }),
                                ]),
                                catchError(error =>
                                    of(
                                        getSiteMetricsStatusFailureAction({
                                            error,
                                        }),
                                    ),
                                ),
                            ),
                    ),
                    mergeMap(merged => merged),
                ),
            ),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
        private readonly http: HttpClient,
        private readonly influxDbService: InfluxdbService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly location: Location,
        private readonly httpBackend: HttpBackend,
    ) {
        this.httpWithoutAuth = new HttpClient(this.httpBackend);
    }
}
